/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {ListDefaultActionsComponent} from '../../common/directives/list-default-actions.component';
import {stateTitleView} from '../../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../../routing/state-utils';
import {CustomEventApiService, CustomEventModel} from './custom-event-api.service';
import {CustomEventEditPageComponent} from './custom-event-edit-page.component';
import {CustomEventRoutePageComponent} from './custom-event-route-page.component';
import {SignalAdminResourceContextActionsComponent} from '../../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../../common/controller/signal-admin-resource-title.component';

export function customEventStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(CustomEventApiService);
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const rh = adnRouterHelper.createRouterHelper(
    'custom-events.custom-event',
    CustomEventApiService,
    'app.adm',
    'app/admin'
  );

  states.add(rh.listPath(), rh.list({
    views: {
      'content@app': {
        component: CustomEventRoutePageComponent,
        bindings: {readOnly: 'listReadOnly'}
      },
      'context@app': {
        component: ListDefaultActionsComponent,
        bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}
      },
      'title@app': stateTitleView()
    }
  }));

  states.add(rh.newItemPath(), rh.newItem());

  states.add(rh.itemPath(), rh.item({
    resolve: {
      customEventPage: function($stateParams: any, readOnly: boolean) {
        if ($stateParams.isNew) {
          return new SignalAdminResourcePageState({
            id: $stateParams.id,
            objectState: 'ACTIVE',
            conversionType: null,
            type: 'COUNT',
            validationWarnings: []
          }, true, readOnly, api, 'Custom Event');
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(model, false, readOnly, api, 'Custom Event'));
      },
      model: function(customEventPage: SignalAdminResourcePageState<CustomEventModel>) {
        return customEventPage.model();
      }
    },
    views: {
      'content@app': {
        component: CustomEventEditPageComponent,
        bindings: {state: 'customEventPage'}
      },
      'context@app': {
        component: SignalAdminResourceContextActionsComponent,
        bindings: {state: 'customEventPage'}
      },
      'title@app': {
        component: SignalAdminResourceTitleComponent,
        bindings: {state: 'customEventPage'}
      }
    }
  }));

  return states.build();
}
